import React from "react";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";

import { EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Column, ORDERING, Record } from "@gsmvalve/gsmvalve-table";
import { t, Trans } from "@lingui/macro";
import { Button, Row, Typography } from "antd";
import type { NextPage } from "next";

import AppLayout from "layout/app";

import EmptyStates from "components/empty-states";
import { ActionMenu } from "components/navigations/header";
import Table, { onHasFilters, pageCountMessage, TableSearch } from "components/table";

import { useApplicationContext } from "contexts/app";

import { IFilter } from "@gsmvalve/gsmvalve-table/dist/cjs/types";
import { UserGroup } from "types";

const limit = 100;

const filters: IFilter[] = [
  { key: "vehicle", omit: true, title: "" },
  { key: "id", omit: true, title: "" },
  { key: "managed_permissions", omit: true, title: "" },
  { key: "account", omit: true, title: "" },
  { key: "url", omit: true, title: "" },
];

const modifier = (data: any) => {
  const records: Record[] = [];

  data?.forEach((rows: any) => {
    rows?.data?.forEach((vehicle: any) => {
      records.push({
        ...vehicle,
      });
    });
  });

  return records;
};

const UserGroups: NextPage = () => {
  const router = useRouter();
  const { session, configuration } = useApplicationContext();

  const accountId = configuration?.account?.id || "";
  const url = `user_groups/?account=${accountId}&ordering=name,id&count=true`;
  const columns: Column[] = [
    {
      key: "name",
      title: t`Name`,
      isVisible: true,
      dataIndex: ["name_display"],
      fixed: true,
      isSortable: true,
      render: (data) => {
        return (
          <Typography.Text ellipsis={true}>
            <Link href={`/settings/user-groups/${data?.id}/?tab=overview`}>{data?.name_display}</Link>
          </Typography.Text>
        );
      },
    },
    { key: "group_type", title: t`Group type`, isVisible: true, fixed: true },
    { key: "group_type", title: t`Group type` },
    {
      key: "actions",
      title: t`Actions`,
      width: 90,
      render: (data) => {
        return (
          <Link href={`/settings/user-groups/${data?.id}/?tab=overview`}>
            <Button type={"text"} icon={<EditOutlined />} />
          </Link>
        );
      },
      isVisible: false,
    },
    { key: "account", omit: true },
    { key: "name_display", omit: true },
    { key: "url", omit: true },
  ];
  const newUserGroupButton = (
    <Button type="primary" onClick={() => router.push("/settings/user-groups/new/")} icon={<PlusCircleOutlined />}>
      <span>
        <Trans>New user group</Trans>
      </span>
    </Button>
  );

  return (
    <AppLayout pageTitle={t`User groups`} fullscreen={true}>
      <ActionMenu
        menus={[
          <Row>
            <TableSearch placeholder={t`Search user groups...`} />
          </Row>,
          newUserGroupButton,
        ]}
      />
      <Table<UserGroup[]>
        key={"users-groups"}
        id={`${session?.accountId}-users-groups`}
        columns={{
          filtersPath: "/user_groups/",
          path: "UserGroup",
          customColumns: columns,
          customFilters: filters,
        }}
        records={{
          url: url,
          sort: [{ key: "name", order: "desc" as ORDERING }],
          limit: limit,
          modifier: modifier,
        }}
        empty={{
          emptyWithoutFilter: (
            <EmptyStates
              title={t`No user groups yet`}
              description={t`Add user groups using the button below.`}
              icon={
                <Image
                  src={"/images/user-groups-empty-state.svg"}
                  width={260}
                  height={260}
                  alt={t`user groups empty state`}
                />
              }
              actions={[newUserGroupButton]}
            />
          ),
          onHasFilter: onHasFilters(),
        }}
        controls={{
          pageCountMessage: pageCountMessage(t`User groups`),
          manageColumn: true,
          filterBtn: true,
        }}
      />
    </AppLayout>
  );
};

export default UserGroups;
